import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { priceFmtNoDecimal, thousandFmt } from '@makyoapp/makyo-common'
import * as Icon from '@makyoapp/makyo-icon/icon'
import { Badge, Button, H6, JustifyVariant, projectPlanColor, TextSwitch } from '@makyoapp/makyo-ui'

type FeatureKey = 'dataRows' | 'customDomain' | 'storage'

const features = [
  {
    key: 'dataRows',
    type: 'number',
  },
  { key: 'customDomain', type: 'boolean' },
  { key: 'storage', type: 'number', unit: 'MB' },
  { key: 'page', type: 'boolean' },
  { key: 'section', type: 'boolean' },
  { key: 'navigationDrawer', type: 'boolean' },
  { key: 'columns', type: 'boolean' },
  { key: 'bar', type: 'boolean' },
  { key: 'tab', type: 'boolean' },
  { key: 'form', type: 'boolean' },
]

export interface Plan {
  id: string
  description: string
  monthly: number
  yearly: number
  features: {
    [key in FeatureKey]: string | number | boolean | Array<{ [k: string]: string | boolean | number }>
  }
}

const data = [
  {
    id: 'FREE',
    description: 'For all regular users who wants to try',
    monthly: 0,
    yearly: 0,
    features: {
      dataRows: 500,
      customDomain: false,
      page: true,
      section: true,
      navigationDrawer: true,
      columns: true,
      bar: true,
      tab: true,
      form: false,
      storage: 500,
    },
  },
  {
    id: 'PRO',
    description: 'For user who want to do more',
    monthly: 9,
    yearly: 9 * 12,
    features: {
      dataRows: 1000,
      customDomain: false,
      storage: 1000,
      page: true,
      section: true,
      navigationDrawer: true,
      columns: true,
      bar: true,
      tab: true,
      form: true,
    },
  },
  {
    id: 'PREMIUM',
    description: 'For most small to medium business',
    monthly: 12,
    yearly: 12 * 12,
    features: {
      dataRows: 10000,
      customDomain: true,
      storage: 5000,
      page: true,
      section: true,
      navigationDrawer: true,
      columns: true,
      bar: true,
      tab: true,
      form: true,
    },
  },
  {
    id: 'ELITE',
    description: 'For user who want to do more',
    monthly: 19,
    yearly: 19 * 12,
    features: {
      dataRows: 50000,
      customDomain: true,
      storage: 10000,
      page: true,
      section: true,
      navigationDrawer: true,
      columns: true,
      bar: true,
      tab: true,
      form: true,
    },
  },
]

interface PricingTableProps {
  /**
   * the id of plan
   */
  activePlan?: string
  onClick: (plan: Plan, idx: number, e: React.MouseEvent<HTMLButtonElement>) => void
}

function renderRow(
  feature: { key: string; type: string; unit?: string },
  plan: Plan,
  index: number,
  activePlan?: string
) {
  const fk = feature.key as FeatureKey
  const isNumber = feature.type === 'number'
  const isBool = feature.type === 'boolean'
  const isActive = activePlan === plan.id

  return (
    <td className={clsx('p-4 min-w-[200px]', isActive && 'active')} key={`${fk}-${index}`}>
      {isBool ? (
        plan.features[fk] ? (
          <Icon.CheckCircle24 className='tick' />
        ) : (
          <Icon.CloseCircle24 className='cross' />
        )
      ) : isNumber ? (
        <>
          {thousandFmt.format(plan.features[fk] as number)} {feature.unit}
        </>
      ) : (
        (plan.features[fk] as React.ReactNode)
      )}
    </td>
  )
}

/**
 * The Pricing table
 */
export default function PricingTable(props: PricingTableProps) {
  const { t } = useTranslation()
  const [isYearly, setIsYearly] = useState<boolean>(false)

  return (
    <div className='overflow-auto w-full'>
      <table className='w-full pricing-table'>
        <thead>
          <tr>
            <th className='min-w-[200px] whitespace-nowrap'>
              <TextSwitch
                justify={JustifyVariant.Center}
                textOn={t('yearly')}
                textOff={t('monthly')}
                keepActiveColor={true}
                checked={isYearly}
                onClick={() => setIsYearly(!isYearly)}
              />
            </th>
            {data.map((plan, idx) => {
              const title = plan.id.toLowerCase()
              const isActive = props.activePlan === plan.id
              return (
                <th className={clsx('min-w-[200px] text-left p-4 align-top', isActive && 'active')} key={idx}>
                  <H6 className='text-center py-2'>
                    <Badge color={projectPlanColor(plan.id)}>
                      {title[0].toUpperCase()}
                      {title.slice(1)}
                    </Badge>
                  </H6>
                  <div
                    className={clsx(
                      'font-normal text-center'
                      //isActive ? 'text-white' : 'text-light'
                    )}
                  >
                    {plan.description}
                  </div>
                  <div className='pb-4 text-center'>
                    <div
                      className={clsx(
                        'pt-4 font-bold text-4xl text-center',
                        /*isActive
                       ? 'text-white'
                       : 'text-primary-600 dark:text-primary-300'*/
                        'text-primary-600 dark:text-primary-300'
                      )}
                    >
                      {priceFmtNoDecimal.format(isYearly ? plan.yearly : plan.monthly)}
                    </div>
                    <span className='font-normal opacity-70 text-center block'>
                      / {isYearly ? t('year') : t('month')}
                    </span>
                  </div>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {features.map((feature) => {
            return (
              <tr key={feature.key} className='odd:bg-zinc-100 dark:odd:bg-slate-600'>
                <th className='ltr:text-left rtl:text-right p-4'>{t(feature.key)}</th>
                {data.map((plan, index) => renderRow(feature, plan, index, props.activePlan))}
              </tr>
            )
          })}
        </tbody>
        <tfoot>
          <tr>
            <td></td>
            {data.map((plan, idx) => {
              const isActive = props.activePlan === plan.id
              return (
                <td className={clsx('p-4', isActive && 'active')} key={`choose-${idx}`}>
                  <Button
                    className='whitespace-nowrap'
                    disabled={props.activePlan ? isActive : false}
                    onClick={(e) => {
                      props.onClick(plan, idx, e)
                    }}
                  >
                    {isActive ? t('Active Plan') : t('Choose Plan')}
                  </Button>
                </td>
              )
            })}
          </tr>
        </tfoot>
      </table>
    </div>
  )
}
